/**
 * Vinyl Carousel Component
 * 
 * A custom web component that creates an infinite horizontal carousel
 * of rotating vinyl records with customizable speed and rotation angles.
 * 
 * @example
 * <vinyl-carousel data-speed="40" data-rotation="225">
 *   ...content...
 * </vinyl-carousel>
 */

class VinylCarousel extends HTMLElement {
  constructor() {
    super();
    
    this.track = null;
    this.speed = 40;
    this.spinSpeed = 3;
    this.verticalPosition = 50;
    this.animationFrame = null;
    this.lastUpdate = 0;
    this.updateInterval = 150; // Update every 150ms for smoother transitions
    this.currentCenterItem = null; // Track current center item to avoid unnecessary updates
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    // Cleanup animation frame
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
  }

  init() {
    // Get settings from data attributes
    const speedAttr = this.getAttribute('data-speed');
    const spinSpeedAttr = this.getAttribute('data-spin-speed');
    const verticalPosAttr = this.getAttribute('data-vertical-position');
    
    this.speed = speedAttr ? parseFloat(speedAttr) : 40;
    this.spinSpeed = spinSpeedAttr ? parseFloat(spinSpeedAttr) : 3;
    this.verticalPosition = verticalPosAttr ? parseFloat(verticalPosAttr) : 50;
    
    // Get the track element
    this.track = this.querySelector('.vinyl-carousel__track');
    
    if (!this.track) {
      console.warn('Vinyl Carousel: track element not found');
      return;
    }

    // Apply CSS custom properties
    this.style.setProperty('--vinyl-speed', `${this.speed}s`);
    this.style.setProperty('--vinyl-spin-speed', `${this.spinSpeed}s`);
    this.style.setProperty('--vinyl-vertical-position', `${this.verticalPosition}%`);

    // Handle reduced motion preference
    this.handleReducedMotion();

    // Start center detection
    this.startCenterDetection();
  }

  /**
   * Start detecting which vinyl is in the center
   */
  startCenterDetection() {
    const updateCenter = (timestamp) => {
      // Throttle updates to every 100ms for better performance
      if (timestamp - this.lastUpdate >= this.updateInterval) {
        this.updateCenterVinyl();
        this.lastUpdate = timestamp;
      }
      
      this.animationFrame = requestAnimationFrame(updateCenter);
    };
    
    // Start the animation loop
    this.animationFrame = requestAnimationFrame(updateCenter);
  }

  /**
   * Update which vinyl is closest to the center
   */
  updateCenterVinyl() {
    const items = this.querySelectorAll('.vinyl-carousel__item');
    if (!items.length) return;

    // Get viewport center
    const viewportCenter = window.innerWidth / 2;
    
    /** @type {Element | null} */
    let closestItem = null;
    let closestDistance = Infinity;

    // Find the vinyl closest to the center
    items.forEach(item => {
      if (item instanceof HTMLElement) {
        const rect = item.getBoundingClientRect();
        const itemCenter = rect.left + rect.width / 2;
        const distance = Math.abs(itemCenter - viewportCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestItem = item;
        }
      }
    });

    // Only update if the center item has changed
    if (closestItem !== this.currentCenterItem) {
      this.currentCenterItem = closestItem;
      
      // Update classes
      items.forEach(item => {
        if (item instanceof HTMLElement) {
          if (item === closestItem) {
            item.classList.add('vinyl-carousel__item--center');
          } else {
            item.classList.remove('vinyl-carousel__item--center');
          }
        }
      });
    }
  }

  /**
   * Public method to change the spin speed
   * @param {number} speed - The new spin speed in seconds
   */
  setSpinSpeed(speed) {
    this.spinSpeed = speed;
    this.style.setProperty('--vinyl-spin-speed', `${speed}s`);
  }

  /**
   * Respect user's reduced motion preferences
   */
  handleReducedMotion() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      if (this.track && this.track instanceof HTMLElement) {
        this.track.style.animationPlayState = 'paused';
      }
    }
  }

  /**
   * Public method to pause the carousel
   */
  pause() {
    if (this.track && this.track instanceof HTMLElement) {
      this.track.style.animationPlayState = 'paused';
    }
  }

  /**
   * Public method to resume the carousel
   */
  resume() {
    if (this.track && this.track instanceof HTMLElement) {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!prefersReducedMotion) {
        this.track.style.animationPlayState = 'running';
      }
    }
  }

  /**
   * Public method to change the speed
   * @param {number} speed - The new speed in seconds
   */
  setSpeed(speed) {
    this.speed = speed;
    this.style.setProperty('--vinyl-speed', `${speed}s`);
  }

}

// Define the custom element
if (!customElements.get('vinyl-carousel')) {
  customElements.define('vinyl-carousel', VinylCarousel);
}

// Add the spin animation keyframes dynamically
const style = document.createElement('style');
style.textContent = `
  @keyframes vinyl-spin {
    from {
      transform: rotate(var(--vinyl-rotation, 225deg));
    }
    to {
      transform: rotate(calc(var(--vinyl-rotation, 225deg) + 360deg));
    }
  }
`;

// Only add the style if it doesn't exist
if (!document.querySelector('#vinyl-carousel-animations')) {
  style.id = 'vinyl-carousel-animations';
  document.head.appendChild(style);
}

// Export for module usage (if needed)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = VinylCarousel;
}

